import React, { useState, useEffect } from 'react';
import {
  Box,
  HStack,
  Text,
  Link,
  Avatar,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalHeader,
  Input,
  Flex,
  useBreakpointValue,
} from '@chakra-ui/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import InvoiceDetails from '../global/invoiceDetails';
import Swal from 'sweetalert2';

const Payments = () => {
  const [activeTab, setActiveTab] = useState('timesheet');
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const itemsPerPage = 5;

  const inputSize = "md";
  const responsiveWidth = useBreakpointValue({ base: "100%", sm: "140px", md: "160px" });

  const responsiveFontSize = useBreakpointValue({ base: "sm", md: "md" });
  const tableMinWidth = useBreakpointValue({ base: "100%", md: "700px" });
  const [submittedInvoices, setSubmittedInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [approvedInvoice, setApprovedInvoice] = useState(null);
  const [rejectedInvoice, setRejectedInvoice] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [failedPayments, setFailedPayments] = useState([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [eventSource, setEventSource] = useState(null);

  const data = [
    {
      id: 1,
      name: 'John Doe',
      jobTitle: 'Nursing Assistant',
      startDate: '2024-12-01',
      endDate: '2024-12-05',
      submissionDate: '2024-12-06',
      timesheetLink: '#',
      invoiceStatus: 'approved',
      invoiceLink: '#',
      paymentStatus: 'paid',
      paymentDate: '2024-12-10',
    },
    {
      id: 2,
      name: 'Jane Smith',
      jobTitle: 'Therapist',
      startDate: '2024-12-02',
      endDate: '2024-12-06',
      submissionDate: '2024-12-07',
      timesheetLink: '#',
      invoiceStatus: 'rejected',
      invoiceLink: '#',
      paymentStatus: 'unpaid',
    },
  ];

  useEffect(() => {
    const fetchSubmittedInvoices = async () => {
      const ticketToken = sessionStorage.getItem('ticketToken');
      // console.log("ticketToken",ticketToken);
      if (!ticketToken) {
        console.error('No ticket token found in sessionStorage');
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/provider/get-submitted-invoices`,
          {
            headers: {
              Authorization: `Bearer ${ticketToken}`
            }
          }
        );
        console.log('Submitted invoices:', response.data);
        setSubmittedInvoices(response.data.data);
      } catch (error) {
        console.error('Error fetching submitted invoices:', error);
      }
    };

    fetchSubmittedInvoices();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'billing') {
      fetchPaymentDetails();
    } else if (activeTab === 'failed transactions') {
      fetchFailedPayments();
    }
  }, [activeTab]);

  const fetchPaymentDetails = async () => {
    const ticketToken = sessionStorage.getItem('ticketToken');
    if (!ticketToken) {
      console.error('No ticket token found in sessionStorage');
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/provider/get-payment-details`,
        {
          headers: {
            Authorization: `Bearer ${ticketToken}`
          }
        }
      );
      console.log('Payment details:', response.data);
      setPaymentDetails(response.data.data);
    } catch (error) {
      console.error('Error fetching payment details:', error);
    }
  };

  const fetchFailedPayments = async () => {
    const ticketToken = sessionStorage.getItem('ticketToken');
    if (!ticketToken) {
      console.error('No ticket token found in sessionStorage');
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/provider/get-failed-payments`,
        {
          headers: {
            Authorization: `Bearer ${ticketToken}`
          }
        }
      );
      console.log('Failed payments:', response.data);
      setFailedPayments(response.data.data);
    } catch (error) {
      console.error('Error fetching failed payments:', error);
    }
  };

  const filteredData = data.filter((row) => {
    const startDate = new Date(row.startDate);
    if (fromDate && startDate < fromDate) return false;
    if (toDate && startDate > toDate) return false;
    return true;
  });

  const getFilteredDataByTab = (tabName) => {
    let sortedData = [...currentData];

    // Apply date filtering
    if (fromDate || toDate) {
      sortedData = sortedData.filter(row => {
        const startDate = new Date(row.jobStartedFrom || row.startDate);
        const endDate = new Date(row.jobEndedOn || row.endDate);
        
        // If fromDate is set, check if employment period starts after fromDate
        if (fromDate && startDate < fromDate) {
          return false;
        }
        
        // If toDate is set, check if employment period ends before toDate
        if (toDate && endDate > toDate) {
          return false;
        }
        
        return true;
      });
    }

    sortedData.sort((a, b) => {
      const dateA = new Date(a.startDate || a.SubmissionDate);
      const dateB = new Date(b.startDate || b.SubmissionDate);
      return dateB - dateA;
    });

    switch (tabName) {
      case 'Requested Timesheet':
        return sortedData.filter(row => row.invoiceStatus === 'submitted');
      case 'Invoices':
        return sortedData.filter(row => ['approved', 'rejected'].includes(row.invoiceStatus));
      case 'Failed Transactions':
        return failedPayments;
      case 'Billing':
        return paymentDetails.filter(row => 
          !['FAILED', 'DECLINED', 'CANCELLED'].includes(row.detailed_payment_state.toUpperCase())
        );
      default:
        return sortedData;
    }
  };

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentData = submittedInvoices.length > 0 ? submittedInvoices : filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  const handleViewTimesheet = (invoice) => {
    console.log("Selected Invoice Full Details:", invoice);
    console.log("Does invoice contain userCountry?", invoice.userCountry ? "Yes" : "No");
    console.log("Does invoice contain ifscCode?", invoice.ifscCode ? "Yes" : "No");
    setSelectedInvoice(invoice);
    onOpen();
  };
  const handleViewSubmissionRecords = (invoice) => {
    console.log("Approved Invoice Full Details:", invoice);
    console.log("Does approved invoice contain userCountry?", invoice.userCountry ? "Yes" : "No");
    console.log("Does approved invoice contain ifscCode?", invoice.ifscCode ? "Yes" : "No");
    setApprovedInvoice(invoice);
    onOpen();
  };
  const handleViewRejectedInvoice = (invoice) => {
    console.log("Rejected Invoice Full Details:", invoice);
    console.log("Does rejected invoice contain userCountry?", invoice.userCountry ? "Yes" : "No");
    console.log("Does rejected invoice contain ifscCode?", invoice.ifscCode ? "Yes" : "No");
    setRejectedInvoice(invoice);
    onOpen();
  };
  const handleModalClose = () => {
    setSelectedInvoice(null);
    setApprovedInvoice(null);
    setRejectedInvoice(null);
    onClose();
  };

  useEffect(() => {
    // Initialize SSE connection
    const initializeEventSource = () => {
      const ticketToken = sessionStorage.getItem('ticketToken');
      if (!ticketToken) {
        console.error('No ticket token found');
        return;
      }

      try {
        const sse = new EventSource(`${process.env.REACT_APP_SERVER_URL}/webhooks/payment-updates`);

        sse.onopen = () => {
          console.log('SSE connection established');
        };

        sse.onmessage = async (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('Payment update received:', data);

            if (data.type === 'payment_update') {
              // Refresh data
              await fetchPaymentDetails();
              await fetchFailedPayments();
              
              // Close any open modals
              handleModalClose();
              
              // Show SweetAlert notification
              const status = data.status || 'updated';
              const icon = status === 'CAPTURED' ? 'success' : 
                         status === 'FAILED' ? 'error' : 'info';
              
              Swal.fire({
                title: 'Payment Update',
                text: `Payment status has been ${status.toLowerCase()}`,
                icon: icon,
                position: 'center',
                timer: 10000,
                timerProgressBar: true,
                showConfirmButton: true,
                confirmButtonText: 'OK',
                confirmButtonColor: '#a5dc86',
                toast: true,
                customClass: {
                  popup: 'colored-toast'
                }
              });
            }
          } catch (error) {
            console.error('Error processing SSE message:', error);
            Swal.fire({
              title: 'Error',
              text: 'Failed to process payment update',
              icon: 'error',
              timer: 10000,
              position: 'center',
              toast: true
            });
          }
        };

        sse.onerror = (error) => {
          console.error('SSE connection error:', error);
          sse.close();
          // Attempt to reconnect after 5 seconds
          setTimeout(initializeEventSource, 5000);
        };

        setEventSource(sse);
      } catch (error) {
        console.error('Error initializing SSE:', error);
      }
    };

    initializeEventSource();

    // Cleanup function
    return () => {
      if (eventSource) {
        console.log('Closing SSE connection');
        eventSource.close();
      }
    };
  }, []); // Empty dependency array means this runs once on component mount

  // Add some custom styles for the colored toast
  useEffect(() => {
    // Add custom styles for the colored toast
    const style = document.createElement('style');
    style.textContent = `
      .colored-toast.swal2-icon-success {
        background-color: #a5dc86 !important;
      }
      .colored-toast.swal2-icon-error {
        background-color: #f27474 !important;
      }
      .colored-toast.swal2-icon-info {
        background-color: #3fc3ee !important;
      }
      .colored-toast {
        color: white !important;
      }
      .colored-toast .swal2-title {
        color: white !important;
      }
      .colored-toast .swal2-close {
        color: white !important;
      }
      .colored-toast .swal2-html-container {
        color: white !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <Box p={5}>
      {/* Date Filter */}
      <Flex justifyContent="flex-end" mb={4} flexWrap="wrap" position="relative" zIndex="2">
        <HStack spacing={4}>
          <DatePicker
            selected={fromDate}
            onChange={(date) => setFromDate(date)}
            customInput={
              <Input
                placeholder="dd/mm/yyyy"
                size={inputSize}
                width={responsiveWidth}
                fontSize={responsiveFontSize}
                borderColor="gray.300"
                borderWidth="1px"
                borderRadius="md"
              />
            }
            dateFormat="dd/MM/yyyy"
            placeholderText="From"
            isClearable
          />
          <DatePicker
            selected={toDate}
            onChange={(date) => setToDate(date)}
            customInput={
              <Input
                placeholder="dd/mm/yyyy"
                size={inputSize}
                width={responsiveWidth}
                fontSize={responsiveFontSize}
                borderColor="gray.300"
                borderWidth="1px"
                borderRadius="md"
              />
            }
            dateFormat="dd/MM/yyyy"
            placeholderText="To"
            isClearable
            popperPlacement="bottom-end"
          />
        </HStack>
      </Flex>

      <Tabs
        variant="unstyled"
        onChange={(index) => setActiveTab(index === 0 ? 'timesheet' : index === 1 ? 'invoices' : index === 2 ? 'failed transactions' : 'billing')}
      >
        <Flex justify="space-between" align="center" mb={6} flexWrap="wrap">
          <TabList>
            <Tab
              fontWeight="bold"
              color={activeTab === 'timesheet' ? 'blue.500' : 'gray.600'}
              _hover={{ backgroundColor: 'transparent' }}
              _selected={{
                textDecoration: 'none',
                color: 'blue.500',
                borderBottom: '2px solid blue.500',
              }}
              px={4}
              pb={2}
            >
              Requested Timesheet
            </Tab>
            <Tab
              fontWeight="bold"
              color={activeTab === 'invoices' ? 'blue.500' : 'gray.600'}
              _hover={{ backgroundColor: 'transparent' }}
              _selected={{
                textDecoration: 'none',
                color: 'blue.500',
                borderBottom: '2px solid blue.500',
              }}
              px={4}
              pb={2}
            >
              Invoices
            </Tab>
            <Tab
              fontWeight="bold"
              color={activeTab === 'failed-transactions' ? 'blue.500' : 'gray.600'}
              _hover={{ backgroundColor: 'transparent' }}
              _selected={{
                textDecoration: 'none',
                color: 'blue.500',
                borderBottom: '2px solid blue.500',
              }}
              px={4}
              pb={2}
            >
              Failed Transactions
            </Tab>
            <Tab
              fontWeight="bold"
              color={activeTab === 'billing' ? 'blue.500' : 'gray.600'}
              _hover={{ backgroundColor: 'transparent' }}
              _selected={{
                textDecoration: 'none',
                color: 'blue.500',
                borderBottom: '2px solid blue.500',
              }}
              px={4}
              pb={2}
            >
              Billing
            </Tab>
          </TabList>
        </Flex>

        <TabPanels>
          {['Requested Timesheet', 'Invoices', 'Failed Transactions', 'Billing'].map((tabName, index) => (
            <TabPanel key={index} px={0}>
              <Box maxH="calc(100vh - 200px)" overflowY="auto">
                <Table variant="simple" width="100%" minWidth={tableMinWidth}>
                  <Thead position="sticky" top={0} bgColor="gray.100" zIndex={1} borderBottom="1px solid" borderColor="gray.200">
                    <Tr>
                      {tabName === 'Failed Transactions' ? (
                        <>
                          <Th p={4}>INVOICE NO</Th>
                          <Th p={4}>EMPLOYEE NAME</Th>
                          <Th p={4}>JOB TITLE</Th>
                          <Th p={4}>EMPLOYMENT PERIOD</Th>
                          <Th p={4}>STATUS</Th>
                          <Th p={4}>FAILURE REASON</Th>
                        </>
                      ) : tabName === 'Billing' ? (
                        <>
                          <Th p={4}>Invoice No</Th>
                          <Th p={4}>Employee Name</Th>
                          <Th p={4}>Job Title</Th>
                          <Th p={4}>Employment Period</Th>
                          <Th p={4}>Payment Date</Th>
                          <Th p={4}>Payment Status</Th>
                        </>
                      ) : (
                        <>
                          {tabName !== 'Billing' && <Th p={4}>Invoice No</Th>}
                          <Th p={4}>Employee Name</Th>
                          <Th p={4}>Job Title</Th>
                          <Th p={4}>Employment Period</Th>
                          {tabName === 'Requested Timesheet' && <Th p={4}>Submission Date</Th>}
                          {tabName === 'Requested Timesheet' && <Th p={4}>Work Duration</Th>}
                          {tabName === 'Invoices' && <Th p={4}>Status</Th>}
                          {tabName === 'Invoices' && <Th p={4}>Remuneration</Th>}
                          {tabName === 'Billing' && <Th p={4}>Payment Date</Th>}
                          <Th p={4}>{tabName === 'Requested Timesheet' ? 'Timesheet' : tabName === 'Invoices' ? 'Submission Records' : tabName === 'Failed Transactions' ? 'Failure Reason' : 'Payment'}</Th>
                        </>
                      )}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {getFilteredDataByTab(tabName).length > 0 ? (
                      getFilteredDataByTab(tabName).map((row) => (
                        <Tr
                          key={row._id}
                          _hover={{
                            transform: 'translateY(-5px)',
                            boxShadow: 'lg',
                            transition: 'transform 0.2s ease-in-out',
                            bg: '#F5F6F8',
                          }}
                        >
                          {tabName === 'Failed Transactions' ? (
                            <>
                             
                              <Td p={4}>{row.invoice_no}</Td>
                              <Td p={4}>{row.employee_name}</Td>
                              <Td p={4}>{row.job_title}</Td>
                              <Td p={4}>
                                {new Date(row.jobStartedFrom).toLocaleDateString()} - {new Date(row.jobEndedOn).toLocaleDateString()}
                              </Td>
                              <Td p={4}>
                                <Text
                                  px={3}
                                  py={1}
                                  borderRadius="md"
                                  display="inline-block"
                                  color="red.500"
                                  bg="red.100"
                                >
                                  {row.detailed_payment_state}
                                </Text>
                              </Td>
                              <Td p={4}>{row.failure_reason}</Td>
                             
                            </>
                          ) : tabName === 'Billing' ? (
                            <>
                              <Td p={4}>{row.invoice_no}</Td>
                              <Td p={4}>{row.employee_name}</Td>
                              <Td p={4}>{row.job_title}</Td>
                              <Td p={4}>
                                {new Date(row.jobStartedFrom || row.startDate).toLocaleDateString()} - {new Date(row.jobEndedOn || row.endDate).toLocaleDateString()}
                              </Td>
                              <Td p={4}>
                                {new Date(row.created_at).toLocaleDateString()}
                              </Td>
                          {/* payment status ----------------------------- */}
                              <Td p={4}>
                                <Text
                                  px={3}
                                  py={1}
                                  borderRadius="md"
                                  display="inline-block"
                                  color={
                                    row.detailed_payment_state === 'COMPLETED' ? 'green.500' :
                                    row.detailed_payment_state === 'CAPTURED' ? 'blue.500' :
                                    'orange.500'
                                  }
                                  bg={
                                    row.detailed_payment_state === 'COMPLETED' ? 'green.100' :
                                    row.detailed_payment_state === 'CAPTURED' ? 'blue.100' :
                                    'orange.100'
                                  }
                                >
                                  {row.detailed_payment_state === 'CAPTURED' ? 'Processing' : row.detailed_payment_state}
                                </Text>
                                {/* payment status end-------------------------------- */}
                              </Td>
                            </>
                          ) : (
                            <>
                              {tabName !== 'Billing' && <Td p={4}>{row.invoiceNo || row.invoiceNumber}</Td>}
                              <Td p={4}>
                                <HStack spacing={3}>
                                  <Text>{row.employeeName || row.name}</Text>
                                </HStack>
                              </Td>
                              <Td p={4}>{row.jobTitle}</Td>
                              <Td p={4}>
                                {new Date(row.jobStartedFrom || row.startDate).toLocaleDateString()} - {new Date(row.jobEndedOn || row.endDate).toLocaleDateString()}
                              </Td>
                              {tabName === 'Requested Timesheet' && (
                                <Td p={4}>
                                  {new Date(row.SubmissionDate).toLocaleDateString()}
                                </Td>
                              )}
                              {tabName === 'Requested Timesheet' && (
                                <Td p={4}>{row.totalWorkingHours}</Td>
                              )}
                              {tabName === 'Invoices' && (
                                <Td p={4} color={row.invoiceStatus === 'approved' ? 'green.500' : 'red.500'}>
                                  {row.invoiceStatus ? row.invoiceStatus.charAt(0).toUpperCase() + row.invoiceStatus.slice(1) : 'Pending'}
                                </Td>
                              )}
                              {tabName === 'Invoices' && (
                                <Td p={4}>{row.totalAmountToBePayable}</Td>
                              )}
                              {tabName === 'Billing' && (
                                <Td p={4}>{row.paymentStatus === 'paid' ? new Date(row.paymentDate).toLocaleDateString() : 'Unpaid'}</Td>
                              )}
                              {tabName === 'Failed Transactions' && (
                                <Td p={4}>
                                  <Text
                                    px={3}
                                    py={1}
                                    borderRadius="md"
                                    display="inline-block"
                                    color="red.500"
                                    bg="red.100"
                                  >
                                    {row.detailed_payment_state}
                                  </Text>
                                </Td>
                              )}
                              {tabName === 'Failed Transactions' && (
                                <Td p={4}>{row.failure_reason || 'Unknown'}</Td>
                              )}
                              <Td p={4}>
                                {tabName === 'Requested Timesheet' ? (
                                  <Button colorScheme="blue" onClick={() => handleViewTimesheet(row)}>
                                    View Timesheet
                                  </Button>
                                ) : tabName === 'Invoices' ? (
                                  row.invoiceStatus === 'approved' ? (
                                    <Button colorScheme="white" textColor="teal.500" onClick={() => handleViewSubmissionRecords(row)}>
                                      View Submission Records
                                    </Button>
                                  ) : row.invoiceStatus === 'rejected' ? (
                                    <Button colorScheme="white" textColor="red.400" onClick={() => handleViewRejectedInvoice(row)}>
                                      Non-Approved Entries
                                    </Button>
                                  ) : null
                                ) : (
                                  row.paymentStatus === 'paid' ? (
                                    <Text color="green.500">Paid</Text>
                                  ) : (
                                    <Text color="red.500">Unpaid</Text>
                                  )
                                )}
                              </Td>
                            </>
                          )}
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={8} textAlign="center" p={4}>
                          No entries found.
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>

              {totalPages > 1 && (
                <HStack spacing={4} mt={6} mb={4} justifyContent="center" flexWrap="wrap">
                  <Button
                    variant="ghost"
                    isDisabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    color="gray.700"
                  >
                    Previous
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <Button
                      key={i + 1}
                      colorScheme={currentPage === i + 1 ? 'gray' : 'gray'}
                      bg={currentPage === i + 1 ? 'gray.500' : 'gray.200'}
                      color={currentPage === i + 1 ? 'white' : 'black'}
                      onClick={() => handlePageChange(i + 1)}
                      borderRadius="md"
                    >
                      {i + 1}
                    </Button>
                  ))}
                  <Button
                    variant="ghost"
                    isDisabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                    color="gray.700"
                  >
                    Next
                  </Button>
                </HStack>
              )}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
      <Modal isOpen={isOpen} onClose={handleModalClose} size="lg">
        <ModalOverlay bg="rgba(0, 0, 0, 0.5)" />
        <ModalContent maxWidth="600px">
          <ModalCloseButton />
          <ModalBody>
            {selectedInvoice && !approvedInvoice && (
              <>
             {selectedInvoice?.resubmissionCount > 0 && (
  <Text color="blue.300" mb="2" textAlign="center" fontSize="sm">
    Please note: This timesheet has been resubmitted and is currently on its 
    <strong> attempt {selectedInvoice.resubmissionCount}</strong>.
  </Text>
)}
              <InvoiceDetails
                jobId={selectedInvoice.jobId}
                applicantName={selectedInvoice.employeeName}
                applicantAddress={selectedInvoice.employeeAddress}
                applicantEmail={selectedInvoice.employeeEmail}
                providerName={selectedInvoice.employerName}
                salary={selectedInvoice.payrate}
                currencyType={selectedInvoice.currencyType}
                jobStartTime={selectedInvoice.jobStartTime}
                jobTitle={selectedInvoice.jobTitle}
                jobEndTime={selectedInvoice.jobEndTime}
                jobStartDate={new Date(selectedInvoice.jobStartedFrom).toLocaleDateString()}
                jobEndDate={selectedInvoice.jobEndedOn}
                totalHours={selectedInvoice.totalWorkingHours}
                totalSalary={selectedInvoice.totalAmountToBePayable}
                submissionDate={new Date(selectedInvoice.SubmissionDate).toLocaleDateString()}
                invoiceNo={selectedInvoice.invoiceNo}
                settlementPeriod={selectedInvoice.settlementPeriod}
                accountName={selectedInvoice.accountName}
                bankName={selectedInvoice.bankName}
                sortCode={selectedInvoice.sortCode}
                accountNumber={selectedInvoice.accountNumber}
                ifscCode={selectedInvoice.ifscCode}
                userCountry={selectedInvoice.userCountry}
                isProvider={true}
                isFromInvoiceTab={false}
                onClose={handleModalClose}
              />
              </>
            )}
            {approvedInvoice && !selectedInvoice && (
              <InvoiceDetails
                jobId={approvedInvoice.jobId}
                applicantName={approvedInvoice.employeeName}
                applicantAddress={approvedInvoice.employeeAddress}
                applicantEmail={approvedInvoice.employeeEmail}
                providerName={approvedInvoice.employerName}
                salary={approvedInvoice.payrate}
                currencyType={approvedInvoice.currencyType}
                jobStartTime={approvedInvoice.jobStartTime}
                jobTitle={approvedInvoice.jobTitle}
                jobEndTime={approvedInvoice.jobEndTime}
                jobStartDate={new Date(approvedInvoice.jobStartedFrom).toLocaleDateString()}
                jobEndDate={approvedInvoice.jobEndedOn}
                totalHours={approvedInvoice.totalWorkingHours}
                totalSalary={approvedInvoice.totalAmountToBePayable}
                submissionDate={new Date(approvedInvoice.SubmissionDate).toLocaleDateString()}
                invoiceNo={approvedInvoice.invoiceNo}
                settlementPeriod={approvedInvoice.settlementPeriod}
                accountName={approvedInvoice.accountName}
                bankName={approvedInvoice.bankName}
                sortCode={approvedInvoice.sortCode}
                accountNumber={approvedInvoice.accountNumber}
                ifscCode={approvedInvoice.ifscCode}
                userCountry={approvedInvoice.userCountry}
                isProvider={false}
                isFromInvoiceTab={true}
                onClose={handleModalClose}
              />
            )}
            {rejectedInvoice && !selectedInvoice && (
              <>
              <Text color="red.500" fontWeight="bold" mb="4">
            This invoice has been rejected due to the following reason:
          </Text>
          <Text fontStyle="italic" mb="4" color="red.400">
            {rejectedInvoice.rejectionReason}
          </Text>
              <InvoiceDetails
                jobId={rejectedInvoice.jobId}
                applicantName={rejectedInvoice.employeeName}
                applicantAddress={rejectedInvoice.employeeAddress}
                applicantEmail={rejectedInvoice.employeeEmail}
                providerName={rejectedInvoice.employerName}
                salary={rejectedInvoice.payrate}
                currencyType={rejectedInvoice.currencyType}
                jobStartTime={rejectedInvoice.jobStartTime}
                jobTitle={rejectedInvoice.jobTitle}
                jobEndTime={rejectedInvoice.jobEndTime}
                jobStartDate={new Date(rejectedInvoice.jobStartedFrom).toLocaleDateString()}
                jobEndDate={rejectedInvoice.jobEndedOn}
                totalHours={rejectedInvoice.totalWorkingHours}
                totalSalary={rejectedInvoice.totalAmountToBePayable}
                submissionDate={new Date(rejectedInvoice.SubmissionDate).toLocaleDateString()}
                invoiceNo={rejectedInvoice.invoiceNo}
                settlementPeriod={rejectedInvoice.settlementPeriod}
                accountName={rejectedInvoice.accountName}
                bankName={rejectedInvoice.bankName}
                sortCode={rejectedInvoice.sortCode}
                accountNumber={rejectedInvoice.accountNumber}
                ifscCode={rejectedInvoice.ifscCode}
                userCountry={rejectedInvoice.userCountry}
                isRejected={true}
                rejectionReason={rejectedInvoice.rejectionReason}
                isprovider={true}
                onClose={handleModalClose}
              />
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Payments;
